



































































































import { Vue, Component, Prop, Watch, Ref } from "vue-property-decorator";
import moment from "moment";
import {
  WEEK_PICKER_MODALITY_DATE_RANGE,
  WEEK_PICKER_MODALITY_WEEK_RANGE,
  WeekPickerIsoWeek,
  WeekPickerResult,
  WeekPickerModality,
} from "@/interfaces/WeekPickerDataTypes";

const STATUS_CLOSED = "closed";
const STATUS_OPENED = "opened";
const STATUS_RANGE_START = "range_start";
const LOAD_MORE_UNIT = "year";
const LOAD_MORE_STEP = 2;

const HOVER_NONE = 0;
const HOVER_WEEK = 2;
const HOVER_MONTH = 4;
const HOVER_WRAPPER = 8;

type WeekPickerStatus =
  | typeof STATUS_CLOSED
  | typeof STATUS_OPENED
  | typeof STATUS_RANGE_START;
type WeekPickerWeek = {
  type: "WeekPickerWeek";
  year: number;
  week: number;
  weekStart: string;
  weekEnd: string;
  disabled: boolean;
};
type WeekPickerMonth = {
  type: "WeekPickerMonth";
  year: number;
  month: number;
  label: string;
  disabled: boolean;
};
type WeekPickerYearLabel = {
  type: "WeekPickerYearLabel";
  year: number;
};
type WeekPickerRow = {
  type: "WeekPickerRow";
  month: WeekPickerMonth;
  weeks: WeekPickerWeek[];
};
type WeekPickerPhysicalRow = WeekPickerYearLabel | WeekPickerRow;
type WeekPickerMouseStatus =
  | typeof HOVER_WEEK
  | typeof HOVER_MONTH
  | typeof HOVER_NONE;

@Component({})
export default class VWeekPicker extends Vue {
  @Prop({
    default: () => {
      WEEK_PICKER_MODALITY_WEEK_RANGE;
    },
  })
  readonly modality!: WeekPickerModality;
  @Prop({ default: "Periodo" }) readonly label!: string;
  @Prop({ default: null }) readonly minDate!: Date | null;
  @Prop({ default: null }) readonly maxDate!: Date | null;
  @Prop({ default: null }) readonly value!: WeekPickerResult;
  @Prop({ default: false }) readonly showReset!: boolean;
  @Prop({ default: false }) readonly monthOnlySelection!: boolean;
  @Ref() readonly menuSelectionWrapper!: HTMLElement;
  @Prop({ default: false }) readonly rightSided!: boolean;

  created() {
    if (this.value) {
      this.previousValue = Object.assign({}, this.value);
    }
    this.currentValue = this.value;
    this._minDate = this.minDate;
    this._maxDate = this.maxDate;
    if (!this._maxDate) {
      this._maxDate = this.today;
    }
    this.loadMore = moment()
      .subtract(LOAD_MORE_STEP, LOAD_MORE_UNIT)
      .startOf("year")
      .toDate();
    if (this._minDate) {
      this.loadMore = moment(this.loadMore).isAfter(moment(this._minDate))
        ? this.loadMore
        : moment(this._minDate).startOf("year").toDate();
    }
    this.rows = this.generateRows(this.loadMore, this._maxDate);
  }

  @Watch("value")
  onValueChanged(newValue: WeekPickerResult) {
    this.currentValue = newValue;
  }

  @Watch("status")
  async onStatusChanged(
    _newValue: WeekPickerStatus,
    oldValue: WeekPickerStatus
  ) {
    if (oldValue === STATUS_CLOSED) {
      await this.$nextTick();
      this.menuSelectionWrapper.scrollTop =
        this.menuSelectionWrapper.scrollHeight;
    }
  }

  get today() {
    return this.$moment().toDate();
  }

  get menuOpened() {
    return this.status !== STATUS_CLOSED;
  }

  get humanReadable() {
    return VWeekPicker.resultToHumanReadable(this.currentValue);
  }

  onClosed() {
    this.status = STATUS_CLOSED;
    if (this.previousValue) {
      this.currentValue = this.previousValue;
      this.notify("input", this.previousValue);
    }
  }
  onReset() {
    this.status = STATUS_CLOSED;
    this.currentValue = null;
    this.notify("input", null);
  }

  onSelect() {
    this.status = STATUS_CLOSED;
    this.previousValue = this.currentValue;
    const response = VWeekPicker.convertResultToModality(
      this.currentValue,
      this.modality
    );
    if (this.modality === WEEK_PICKER_MODALITY_DATE_RANGE && response) {
      if (this._minDate) {
        if (moment(response.start).isBefore(moment(this._minDate))) {
          response.start = this._minDate;
        }
      }
      if (this._maxDate) {
        if (moment(this._maxDate).isBefore(moment(response.end))) {
          response.end = this._maxDate;
        }
      }
      this.currentValue = response;
    }
    this.notify("input", response);
  }

  onInputClick() {
    if (this.status === STATUS_CLOSED) {
      this.status = STATUS_OPENED;
    }
  }

  onClickOutside() {
    if (this.status !== STATUS_CLOSED) {
      this.onClosed();
    }
  }

  monthClass(row: WeekPickerRow) {
    const validWeeks = row.weeks.filter((w) => !w.disabled);
    const [start, end] = this.firstAndLastIsoWeek(validWeeks);
    if (
      this.isBetweenCurrentSelection(start) &&
      this.isBetweenCurrentSelection(end)
    ) {
      return "selected";
    }
    if (this.status === STATUS_RANGE_START && this.mouseStatus !== HOVER_NONE) {
      if (
        this.isBetweenShallowSelection(start) &&
        this.isBetweenShallowSelection(end)
      ) {
        return "shallow";
      }
    }
    return "";
  }
  weekClass(week: WeekPickerWeek) {
    if (week.disabled) {
      return "disabled";
    }
    const isoWeek = this.weekToIsoWeek(week);
    if (this.isBetweenCurrentSelection(isoWeek)) {
      return "selected";
    }
    if (this.mouseStatus !== HOVER_NONE && this.status === STATUS_RANGE_START) {
      if (this.hasMouseStatus(HOVER_MONTH)) {
        const row = this.currentlyOver as WeekPickerRow;
        if (
          row.weeks.find((w) => w.week === week.week && w.year === week.year)
        ) {
          return "shallow";
        }
      }
      if (this.isBetweenShallowSelection(isoWeek)) {
        return "shallow";
      }
    }
    return "";
  }

  onCalendarMouseOver(row: WeekPickerRow, week: WeekPickerWeek) {
    if (this.monthOnlySelection) {
      this.onMonthMouseOver(row);
    } else {
      this.onWeekMouseOver(week);
    }
  }

  onWeekMouseOver(week: WeekPickerWeek) {
    this.currentlyOver = week;
    if (this.status === STATUS_RANGE_START) {
      this.shallowSelection = { week: week.week, year: week.year };
    } else {
      this.shallowSelection = null;
    }
  }
  onMonthMouseOver(row: WeekPickerRow) {
    this.currentlyOver = row;
    if (this.status === STATUS_RANGE_START) {
      const value = VWeekPicker.convertResultToModality(
        this.currentValue,
        WEEK_PICKER_MODALITY_WEEK_RANGE
      );
      if (value) {
        const [firstWeek, lastWeek] = this.firstAndLastIsoWeek(row.weeks);
        if (
          this.isStrictlyBeforeIsoWeek(
            firstWeek,
            value.start as WeekPickerIsoWeek
          )
        ) {
          this.shallowSelection = firstWeek;
        }
        if (
          this.isStrictlyAfterIsoWeek(lastWeek, value.end as WeekPickerIsoWeek)
        ) {
          this.shallowSelection = lastWeek;
        }
      }
    } else {
      this.shallowSelection = null;
    }
  }

  onWrapperMouseEnter() {
    this.addMouseStatus(HOVER_WRAPPER);
  }

  onWrapperMouseLeave() {
    this.removeMouseStatus(HOVER_WRAPPER);
  }

  async onWrapperScroll() {
    const currentYear = this.computeCurrentYear();
    if (this.menuSelectionWrapper?.scrollTop <= 29) {
      this.currentYear = currentYear + 1;
    } else {
      this.currentYear = currentYear;
    }
    if (
      this._minDate &&
      moment(this.loadMore).isSameOrAfter(moment(this._minDate))
    ) {
      return;
    }
    if (this.menuSelectionWrapper?.scrollTop <= 200) {
      const { scrollTop, scrollHeight } = this.menuSelectionWrapper;
      this.onLoadMore();
      await this.$nextTick();
      const delta = this.menuSelectionWrapper.scrollHeight - scrollHeight;
      this.menuSelectionWrapper.scrollTop = scrollTop + delta;
    }
  }

  onMonthMouseEnter() {
    this.addMouseStatus(HOVER_MONTH);
  }

  onMonthMouseLeave() {
    this.removeMouseStatus(HOVER_MONTH);
  }

  onWeekMouseEnter() {
    this.addMouseStatus(HOVER_WEEK);
  }

  onWeekMouseLeave() {
    this.removeMouseStatus(HOVER_WEEK);
  }

  static resultToHumanReadable(value: WeekPickerResult): string {
    value = VWeekPicker.convertResultToModality(
      value,
      WEEK_PICKER_MODALITY_WEEK_RANGE
    );
    if (value && value.type === WEEK_PICKER_MODALITY_WEEK_RANGE) {
      if (value.start.year === value.end.year) {
        return `W${value.start.week}-W${value.end.week}/${value.start.year}`;
      } else {
        return `W${value.start.week}/${value.start.year}-W${value.end.week}/${value.end.year}`;
      }
    }
    return "";
  }

  static convertResultToModality(
    value: WeekPickerResult,
    modality: WeekPickerModality
  ): WeekPickerResult {
    if (value && value.type !== modality) {
      if (modality === WEEK_PICKER_MODALITY_WEEK_RANGE) {
        const start = value.start as Date;
        const startMoment = moment(start);
        const convertedStart = {
          year: startMoment.isoWeekYear(),
          week: startMoment.isoWeek(),
        };
        const end = value.end as Date;
        const endMoment = moment(end);
        const convertedEnd = {
          year: endMoment.isoWeekYear(),
          week: endMoment.isoWeek(),
        };
        return { start: convertedStart, end: convertedEnd, type: modality };
      }
      if (modality === WEEK_PICKER_MODALITY_DATE_RANGE) {
        const start = value.start as WeekPickerIsoWeek;
        const convertedStart = VWeekPicker.isoWeekToMoment(start)
          .startOf("W")
          .toDate();
        const end = value.end as WeekPickerIsoWeek;
        const convertedEnd = VWeekPicker.isoWeekToMoment(end)
          .endOf("W")
          .toDate();
        return { start: convertedStart, end: convertedEnd, type: modality };
      }
    }
    return value;
  }

  private rows: WeekPickerPhysicalRow[] = [];
  private _minDate: Date | null = null;
  private _maxDate: Date | null = null;
  private previousValue: WeekPickerResult = null;
  private currentValue: WeekPickerResult = null;
  private status: WeekPickerStatus = STATUS_CLOSED;
  private shallowSelection: WeekPickerIsoWeek | null = null;
  private mouseStatus: WeekPickerMouseStatus = HOVER_NONE;
  private currentlyOver: WeekPickerWeek | WeekPickerRow | null = null;
  private currentYear = 0;
  private loadMore: Date = moment().toDate();

  private notify(msg: string, data?: WeekPickerResult) {
    this.$emit(msg, data);
  }

  private generateWeeks(
    startDate: Date,
    endDate: Date,
    month: number
  ): WeekPickerWeek[] {
    const results: WeekPickerWeek[] = [];
    const momentEnd = this.$moment(endDate).endOf("W");
    for (
      let momentStart = this.$moment(startDate).isoWeekday(4);
      momentStart.isSameOrBefore(momentEnd);
      momentStart = momentStart.add(1, "week")
    ) {
      const rweek = momentStart.isoWeek();
      const rmonth = moment()
        .isoWeekYear(momentStart.isoWeekYear())
        .isoWeek(rweek)
        .isoWeekday(4);
      if (rmonth.month() !== month) {
        continue;
      }
      const week: WeekPickerWeek = {
        type: "WeekPickerWeek",
        year: momentStart.isoWeekYear(),
        weekStart: momentStart.startOf("W").format("D"),
        weekEnd: momentStart.endOf("W").format("D"),
        week: momentStart.isoWeek(),
        disabled: false,
      };
      if (this.isWeekDisabled(week)) {
        week.disabled = true;
      }
      results.push(week);
    }
    return results;
  }

  private generateRows(startDate: Date, endDate: Date) {
    const results: WeekPickerPhysicalRow[] = [];

    if (this._maxDate && moment(endDate).isAfter(moment(this._maxDate))) {
      endDate = this._maxDate;
    }
    if (this._minDate && moment(startDate).isBefore(moment(this._minDate))) {
      startDate = this._minDate;
    }
    const momentEnd = this.$moment(endDate).endOf("month");

    for (
      let momentStart = this.$moment(startDate).startOf("month");
      momentStart.isSameOrBefore(momentEnd);
      momentStart = momentStart.add(1, "month")
    ) {
      const month: WeekPickerMonth = {
        type: "WeekPickerMonth",
        year: momentStart.isoWeekYear(),
        month: momentStart.month(),
        label: momentStart.format("MMMM"),
        disabled: false,
      };
      const row: WeekPickerRow = {
        type: "WeekPickerRow",
        month,
        weeks: this.generateWeeks(
          momentStart.startOf("month").toDate(),
          momentStart.endOf("month").toDate(),
          month.month
        ),
      };
      if (momentStart.month() === 0) {
        results.push({
          type: "WeekPickerYearLabel",
          year: momentStart.isoWeekYear(),
        });
      }
      results.push(row);
    }
    return results;
  }

  private onCalendarClick(row: WeekPickerRow, week: WeekPickerWeek) {
    if (this.monthOnlySelection) {
      this.onMonthClick(row);
    } else {
      this.onWeekClick(week);
    }
  }

  private onMonthClick(row: WeekPickerRow) {
    const validWeeks = row.weeks.filter((w) => !w.disabled);
    const [firstWeek, lastWeek] = this.firstAndLastIsoWeek(validWeeks);
    if (this.status === STATUS_OPENED) {
      this.currentValue = {
        type: WEEK_PICKER_MODALITY_WEEK_RANGE,
        start: firstWeek,
        end: lastWeek,
      };
      this.status = STATUS_RANGE_START;
    } else if (this.status === STATUS_RANGE_START) {
      this.currentValue = VWeekPicker.convertResultToModality(
        this.currentValue,
        WEEK_PICKER_MODALITY_WEEK_RANGE
      );
      if (this.currentValue) {
        const start = this.currentValue.start as WeekPickerIsoWeek;
        const end = this.currentValue.end as WeekPickerIsoWeek;

        if (this.isStrictlyBeforeIsoWeek(lastWeek, start)) {
          this.currentValue = Object.assign({}, this.currentValue, {
            start: firstWeek,
          });
        } else if (this.isStrictlyAfterIsoWeek(firstWeek, end)) {
          this.currentValue = Object.assign({}, this.currentValue, {
            end: lastWeek,
          });
        } else {
          this.currentValue = {
            type: WEEK_PICKER_MODALITY_WEEK_RANGE,
            start: firstWeek,
            end: lastWeek,
          };
        }
      }
      this.status = STATUS_OPENED;
    }
  }

  private onWeekClick(week: WeekPickerWeek) {
    if (week.disabled) {
      return;
    }
    if (this.status === STATUS_OPENED) {
      this.currentValue = {
        type: WEEK_PICKER_MODALITY_WEEK_RANGE,
        start: { week: week.week, year: week.year },
        end: { week: week.week, year: week.year },
      };
      this.status = STATUS_RANGE_START;
    } else if (this.status === STATUS_RANGE_START) {
      const momentWeek = VWeekPicker.isoWeekToMoment(week);
      this.currentValue = VWeekPicker.convertResultToModality(
        this.currentValue,
        WEEK_PICKER_MODALITY_WEEK_RANGE
      );
      if (this.currentValue) {
        const start = this.currentValue.start as WeekPickerIsoWeek;
        const momentStart = VWeekPicker.isoWeekToMoment(start);
        if (momentWeek.isBefore(momentStart)) {
          this.currentValue = Object.assign({}, this.currentValue, {
            start: this.weekToIsoWeek(week),
          });
        } else {
          this.currentValue = Object.assign({}, this.currentValue, {
            end: this.weekToIsoWeek(week),
          });
        }
        this.status = STATUS_OPENED;
      }
    }
  }

  private isWeekDisabled(week: WeekPickerWeek) {
    if (this._maxDate) {
      const mxDate = this.dateToIsoWeek(this._maxDate);
      if (this.isStrictlyAfterIsoWeek(this.weekToIsoWeek(week), mxDate)) {
        return true;
      }
    }
    if (this._minDate) {
      const mnDate = this.dateToIsoWeek(this._minDate);
      if (this.isStrictlyBeforeIsoWeek(this.weekToIsoWeek(week), mnDate)) {
        return true;
      }
    }
    return false;
  }

  private isBetweenShallowSelection(week: WeekPickerIsoWeek) {
    const value = VWeekPicker.convertResultToModality(
      this.currentValue,
      WEEK_PICKER_MODALITY_WEEK_RANGE
    );
    if (value && this.shallowSelection) {
      const start = value.start as WeekPickerIsoWeek;
      const end = value.end as WeekPickerIsoWeek;
      return (
        this.isBetweenRange(week, this.shallowSelection, start) ||
        this.isBetweenRange(week, end, this.shallowSelection)
      );
    }
    return false;
  }

  private isBetweenCurrentSelection(week: WeekPickerIsoWeek) {
    const value = VWeekPicker.convertResultToModality(
      this.currentValue,
      WEEK_PICKER_MODALITY_WEEK_RANGE
    );
    if (value) {
      return this.isBetweenRange(
        week,
        value.start as WeekPickerIsoWeek,
        value.end as WeekPickerIsoWeek
      );
    }
    return false;
  }

  private isBetweenRange(
    checkWeek: WeekPickerIsoWeek,
    start: WeekPickerIsoWeek,
    end: WeekPickerIsoWeek
  ) {
    return (
      this.isAfterIsoWeek(checkWeek, start) &&
      this.isBeforeIsoWeek(checkWeek, end)
    );
  }

  private isStrictlyBeforeIsoWeek(
    checkWeek: WeekPickerIsoWeek,
    targetWeek: WeekPickerIsoWeek
  ) {
    return this.isBeforeIsoWeek(checkWeek, targetWeek, true);
  }
  private isStrictlyAfterIsoWeek(
    checkWeek: WeekPickerIsoWeek,
    targetWeek: WeekPickerIsoWeek
  ) {
    return this.isAfterIsoWeek(checkWeek, targetWeek, true);
  }
  private aroundIsoWeek(
    start: WeekPickerIsoWeek,
    end: WeekPickerIsoWeek,
    target: WeekPickerIsoWeek
  ): boolean {
    const checkStart = this.isBeforeIsoWeek(start, target);
    const checkEnd = this.isAfterIsoWeek(end, target);
    return checkStart && checkEnd;
  }

  private isBeforeIsoWeek(
    checkWeek: WeekPickerIsoWeek,
    targetWeek: WeekPickerIsoWeek,
    strict = false
  ) {
    const convertedCheckWeek = this.isoWeekToString(checkWeek);
    const convertedTargetWeek = this.isoWeekToString(targetWeek);
    if (strict) {
      return convertedCheckWeek < convertedTargetWeek;
    } else {
      return convertedCheckWeek <= convertedTargetWeek;
    }
  }
  private isAfterIsoWeek(
    checkWeek: WeekPickerIsoWeek,
    targetWeek: WeekPickerIsoWeek,
    strict = false
  ) {
    const convertedCheckWeek = this.isoWeekToString(checkWeek);
    const convertedTargetWeek = this.isoWeekToString(targetWeek);
    if (strict) {
      return convertedCheckWeek > convertedTargetWeek;
    } else {
      return convertedCheckWeek >= convertedTargetWeek;
    }
  }
  private visibleRows() {
    const el: HTMLElement[] = [];
    if (this.menuSelectionWrapper) {
      const cTop = this.menuSelectionWrapper.scrollTop;
      const cBottom = cTop + this.menuSelectionWrapper.clientHeight;
      const rows = document.querySelectorAll(
        "div.week-picker__menu-selection__row"
      );
      rows.forEach((r: Element) => {
        const eTop = (r as HTMLElement).offsetTop - 50;
        const eBottom = eTop + (r as HTMLElement).clientHeight - 50;
        if (eTop >= cTop && eBottom <= cBottom) {
          el.push(r as HTMLElement);
        }
      });
    }
    return el;
  }

  private computeCurrentYear() {
    return this.visibleRows().reduce<number>(
      (currentMin: number, row: HTMLElement) => {
        if (row.dataset.year) {
          const year = Number.parseInt(row.dataset.year);
          if (year < currentMin) {
            return year;
          }
        }
        return currentMin;
      },
      2050
    );
  }

  private onLoadMore() {
    const upperLimit = moment(this.loadMore).subtract(1, "month").toDate();
    this.loadMore = moment(this.loadMore)
      .subtract(LOAD_MORE_STEP, LOAD_MORE_UNIT)
      .startOf("year")
      .toDate();
    if (this._minDate) {
      this.loadMore = moment(this.loadMore).isAfter(moment(this._minDate))
        ? this.loadMore
        : moment(this._minDate).startOf("year").toDate();
    }
    this.rows = [...this.generateRows(this.loadMore, upperLimit), ...this.rows];
  }

  private addMouseStatus(status: number) {
    this.mouseStatus |= status;
  }

  private removeMouseStatus(status: number) {
    this.mouseStatus &= ~status;
  }

  private hasMouseStatus(status: number): boolean {
    return !!(this.mouseStatus & status);
  }

  private weekToIsoWeek(week: WeekPickerWeek): WeekPickerIsoWeek {
    return { week: week.week, year: week.year };
  }

  private firstAndLastIsoWeek(weeks: WeekPickerWeek[]): WeekPickerIsoWeek[] {
    return [
      this.weekToIsoWeek(weeks[0]),
      this.weekToIsoWeek(weeks[weeks.length - 1]),
    ];
  }

  private isoWeekToString(week: WeekPickerIsoWeek): string {
    const paddedWeek = `${week.week}`.padStart(2, "0");
    return `${week.year}-${paddedWeek}`;
  }

  static isoWeekToMoment(week: WeekPickerIsoWeek): moment.Moment {
    return moment().isoWeekYear(week.year).isoWeek(week.week);
  }

  private isoWeekToDate(week: WeekPickerIsoWeek): Date {
    return VWeekPicker.isoWeekToMoment(week).toDate();
  }

  private dateToIsoWeek(date: Date): WeekPickerIsoWeek {
    const convertedDate = moment(date);
    return { year: convertedDate.isoWeekYear(), week: convertedDate.isoWeek() };
  }
}
